import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { useState, useEffect, useMemo, useCallback } from 'react';
import { XMarkIcon } from '@iris/react-icons/XMarkIcon';
import { simulateButtonKeyboardBehavior } from '../../utils/buttonKeyboardBehavior.esm.js';
import filterChipStyles from './FilterChip.styles.esm.js';
import { formatThousands } from './utils.esm.js';

const FilterChip = React__default.forwardRef((_a, ref) => {
    var { selected = false, quantity, inverse = false, text, onClick, className, showQuantity = false, "aria-label": ariaLabel, disabled = false } = _a, props = __rest(_a, ["selected", "quantity", "inverse", "text", "onClick", "className", "showQuantity", "aria-label", "disabled"]);
    const [internalSelected, setInternalSelected] = useState(selected);
    useEffect(() => {
        setInternalSelected(selected);
    }, [selected]);
    const styles = useMemo(() => filterChipStyles({ selected: internalSelected, inverse, disabled }), [internalSelected, inverse, disabled]);
    const { base, quantity: quantityClasses, icon, wrapper } = styles;
    const handleClick = useCallback((event) => {
        setInternalSelected(!internalSelected);
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [internalSelected, onClick]);
    const handleKeyDown = useCallback((e) => simulateButtonKeyboardBehavior(onClick)(e), [onClick]);
    const renderQuantity = () => {
        if (!internalSelected && showQuantity && quantity !== undefined) {
            return (jsxs("div", { "aria-hidden": "true", className: quantityClasses(), children: ["(", formatThousands(quantity), ")"] }));
        }
        return null;
    };
    const renderCloseControl = () => {
        if (internalSelected) {
            return jsx(XMarkIcon, { "aria-hidden": "true", className: icon() });
        }
        return null;
    };
    const combinedAriaLabel = useMemo(() => {
        const chipAriaLabel = internalSelected
            ? "Activate to remove."
            : `${showQuantity ? quantity : 0} selections available. Activate to select.`;
        return ariaLabel
            ? `${ariaLabel} ${chipAriaLabel}`
            : `${text}. ${chipAriaLabel}`;
    }, [internalSelected, showQuantity, quantity, ariaLabel, text]);
    return (jsx("button", Object.assign({ ref: ref, "aria-label": combinedAriaLabel, className: base({ class: className }), onClick: handleClick, onKeyDown: handleKeyDown }, props, { children: jsxs("div", { className: wrapper(), children: [jsx("span", { "aria-hidden": "true", children: text }), renderQuantity(), renderCloseControl()] }) })));
});
FilterChip.displayName = "FilterChip";

export { FilterChip };
